import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

const MAX_ATTEMPTS = 8;
const INITIAL_DELAY = 1500;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, checkAuth } = useAuthStore();
  const [status, setStatus] = useState('checking');
  const [planInfo, setPlanInfo] = useState(null);
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const pollingRef = useRef(null);
  const sessionId = searchParams.get('session_id');

  const clearPolling = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId) return;

    if (attempts >= MAX_ATTEMPTS) {
      setStatus('timeout');
      return;
    }

    try {
      const delay = Math.min(INITIAL_DELAY * Math.pow(1.5, attempts), 10000);
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.status === 404) {
        // Session not found — might be a momentary delay, retry with backoff
        setErrorMessage('Statut temporairement indisponible');
        pollingRef.current = setTimeout(() => pollStatus(attempts + 1), delay);
        return;
      }

      if (response.status === 500) {
        // Server error — retry with longer backoff
        setErrorMessage('Erreur serveur, nouvelle tentative...');
        pollingRef.current = setTimeout(() => pollStatus(attempts + 1), delay * 1.5);
        return;
      }

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();

      // Check payment status: explicit 'paid', or 'complete' without explicit payment_status (DEV mode)
      if (data.payment_status === 'paid' || (data.status === 'complete' && !data.payment_status)) {
        setPlanInfo(data.plan_details || null);
        setCreditsAdded(data.credits_added || 0);
        setStatus('success');
        await checkAuth();
        toast.success('Paiement réussi !');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Still pending — continue polling with backoff
        setErrorMessage('');
        pollingRef.current = setTimeout(() => pollStatus(attempts + 1), delay);
      }
    } catch (error) {
      setErrorMessage('Problème de connexion');
      const delay = Math.min(INITIAL_DELAY * Math.pow(1.5, attempts), 10000);
      pollingRef.current = setTimeout(() => pollStatus(attempts + 1), delay);
    }
  }, [sessionId, token, checkAuth]);

  const handleManualRetry = () => {
    setStatus('checking');
    setErrorMessage('');
    setPlanInfo(null);
    clearPolling();
    pollStatus(0);
  };

  useEffect(() => {
    if (!sessionId) {
      navigate('/home');
      return;
    }

    pollStatus(0);

    return () => clearPolling();
  }, [sessionId, token, navigate, checkAuth, pollStatus]);

  return (
    <div className="h-full w-full flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-light rounded-2xl p-8 text-center max-w-sm w-full"
      >
        {status === 'checking' && (
          <>
            <div className="spinner mx-auto mb-4" />
            <p className="text-white font-bold">Vérification du paiement...</p>
            {errorMessage && (
              <p className="text-yellow-400 text-sm mt-2">{errorMessage}</p>
            )}
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <p className="text-white font-bold text-xl">Paiement réussi !</p>
            <p className="text-muted-foreground mt-2">Vos crédits ont été ajoutés.</p>
            {planInfo && (
              <div className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-left">
                <p className="text-sm text-green-300 font-semibold">
                  Plan souscrit : {planInfo.name}
                </p>
                {creditsAdded > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    +{creditsAdded} crédits ajoutés
                  </p>
                )}
              </div>
            )}
            <button
              onClick={() => navigate('/subscription')}
              className="mt-4 btn-primary w-full flex items-center justify-center gap-2"
            >
              <ExternalLink size={16} />
              Gérer mon abonnement
            </button>
            <button
              onClick={() => navigate('/home')}
              className="mt-2 text-muted-foreground text-sm hover:text-white transition-colors"
            >
              Retour à l'accueil
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-bold text-xl">Session expirée</p>
            <p className="text-muted-foreground mt-2">
              Le délai de confirmation a été dépassé. Veuillez réessayer.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => navigate('/subscription')}
                className="w-full btn-primary"
              >
                Réessayer
              </button>
              <button
                onClick={handleManualRetry}
                className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} />
                Vérifier à nouveau
              </button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <AlertCircle size={48} className="text-yellow-400 mx-auto mb-4" />
            <p className="text-yellow-400 font-bold">Vérification en attente</p>
            <p className="text-muted-foreground mt-2 text-sm">
              Le paiement a bien été initié mais la confirmation prend plus de temps que prévu.
            </p>
            <p className="text-muted-foreground mt-2 text-xs">
              Veuillez vérifier votre email pour la confirmation. Si le problème persiste, contactez le support à{' '}
              <a href="mailto:support@soviewfx.fr" className="text-primary underline">support@soviewfx.fr</a>.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={handleManualRetry}
                className="w-full btn-primary flex items-center justify-center gap-2"
                disabled={status === 'checking'}
              >
                <RefreshCw size={16} />
                Vérifier le statut du paiement
              </button>
              <button
                onClick={() => navigate('/subscription')}
                className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium hover:bg-white/10 transition-colors"
              >
                Voir les abonnements
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
